import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { Download, Search } from 'lucide-react';

const typeFilters = [
  { value: '', label: 'All Types' }, { value: 'DEPOSIT', label: 'Deposit' }, { value: 'WITHDRAWAL', label: 'Withdrawal' },
  { value: 'ROI_EARNING', label: 'ROI' }, { value: 'DIRECT_COMMISSION', label: 'Direct Commission' },
  { value: 'LEVEL_COMMISSION', label: 'Level Commission' }, { value: 'BONUS', label: 'Bonus' },
  { value: 'INVESTMENT', label: 'Investment' }, { value: 'ADJUSTMENT', label: 'Adjustment' },
];

export default function AdminTransactionsPage() {
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['adminTransactions', typeFilter, search, page],
    queryFn: () => api.get('/admin/transactions', { params: { type: typeFilter, search, page, limit: 20 } }).then(r => r.data),
  });

  const transactions = data?.data || [];
  const meta = data?.meta || { total: 0, page: 1, totalPages: 1 };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-white">Transactions</h1><p className="text-sm text-slate-400">{meta.total} total transactions</p></div>
        <Button variant="secondary" size="sm"><Download className="w-4 h-4 mr-1" />Export</Button>
      </div>
      <Card><CardContent className="py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Search transactions..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/[0.02] text-white placeholder-slate-500" />
          </div>
          <Select options={typeFilters} value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} />
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-white/[0.06] bg-white/[0.02]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">User</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Balance After</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Description</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Date</th>
            </tr></thead>
            <tbody className="divide-y divide-white/[0.04]">
              {isLoading ? <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">Loading...</td></tr> :
                transactions.length === 0 ? <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No transactions found</td></tr> :
                transactions.map((tx: any) => (
                  <tr key={tx.id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3"><Badge status={tx.type} /></td>
                    <td className="px-4 py-3 text-sm text-slate-300">{tx.wallet?.user?.username || '-'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-white">{formatCurrency(tx.amount)}</td>
                    <td className="px-4 py-3 text-sm text-slate-400">{formatCurrency(tx.balanceAfter)}</td>
                    <td className="px-4 py-3"><Badge status={tx.status} /></td>
                    <td className="px-4 py-3 text-sm text-slate-400 max-w-[200px] truncate">{tx.description || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-400">{new Date(tx.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {meta.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
            <p className="text-sm text-slate-400">Page {meta.page} of {meta.totalPages}</p>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button variant="secondary" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}
